import axios from "axios";
import { url, AuthHeader } from "../../utils/_exports";
import {
	ControlCenterResponse,
	ControlCenterCountsResponse,
	ControlCenterPaginatedResponse,
	UpdateReportStatusResponse,
	ControlCenterFilters,
	FilterOptionsResponse,
	ControlCenterView,
} from "../../types/controlCenter.types";

export const getControlCenterReports =
	async (): Promise<ControlCenterResponse> => {
		try {
			const response = await axios.get(`${url}/control_center/reports`, {
				...AuthHeader,
			});
			return response.data;
		} catch (error) {
			console.error("Failed to get control center reports", error);
			throw error;
		}
	};

export const getControlCenterCounts = async (
	view: ControlCenterView = "task"
): Promise<ControlCenterCountsResponse> => {
	try {
		const response = await axios.get(`${url}/control_center/counts`, {
			params: { view },
			...AuthHeader,
		});
		return response.data;
	} catch (error) {
		console.error("Failed to get control center counts", error);
		throw error;
	}
};

export const getControlCenterFilterOptions = async (
	view: ControlCenterView = "task"
): Promise<FilterOptionsResponse> => {
	try {
		const response = await axios.get(`${url}/control_center/filter-options`, {
			params: { view },
			...AuthHeader,
		});
		return response.data;
	} catch (error) {
		console.error("Failed to get control center filter options", error);
		throw error;
	}
};

export const getControlCenterReportsBySection = async (
	section: string,
	limit: number | "all" = 7,
	offset: number = 0,
	filters?: ControlCenterFilters
): Promise<ControlCenterPaginatedResponse> => {
	try {
		const params: Record<string, string | number | undefined> = {
			section,
			limit,
			offset,
		};

		if (filters) {
			if (filters.search) params.search = filters.search;
			if (filters.report_title) params.report_title = filters.report_title;
			if (filters.project_name) params.project_name = filters.project_name;
			if (filters.assigned_to) params.assigned_to = filters.assigned_to;
			if (filters.report_types?.length)
				params.report_types = filters.report_types.join(",");
			if (filters.combined_statuses?.length)
				params.combined_statuses = filters.combined_statuses.join(",");
			if (filters.tenants?.length) params.tenants = filters.tenants.join(",");
			if (filters.task_names?.length)
				params.task_names = filters.task_names.join(",");
			if (filters.projects?.length)
				params.projects = filters.projects.join(",");
			if (filters.wells?.length) params.wells = filters.wells.join(",");
			if (filters.operations?.length)
				params.operations = filters.operations.join(",");
			if (filters.sort_key) {
				params.sort_key = filters.sort_key;
				params.sort_direction = filters.sort_direction ?? -1;
			}
			if (filters.view) params.view = filters.view;
		}

		const response = await axios.get(`${url}/control_center/reports`, {
			params,
			...AuthHeader,
		});
		return response.data;
	} catch (error) {
		console.error(
			`Failed to get control center reports for section ${section}`,
			error
		);
		throw error;
	}
};

export const refreshControlCenter = async (
	view: ControlCenterView = "task"
): Promise<ControlCenterCountsResponse> => {
	try {
		const response = await axios.post(
			`${url}/control_center/refresh`,
			{},
			{
				params: { view },
				...AuthHeader,
			}
		);
		return response.data;
	} catch (error) {
		console.error("Failed to refresh control center", error);
		throw error;
	}
};

export const updateReportStatus = async (
	reportType: string,
	reportId: string,
	status: string,
	reason?: string,
	reassignTo?: string,
	isRejected?: boolean
): Promise<UpdateReportStatusResponse> => {
	try {
		const body: Record<string, string | boolean | undefined> = { status };
		if (reason) body.reason = reason;
		if (reassignTo) body.reassign_to = reassignTo;
		if (isRejected !== undefined) body.is_rejected = isRejected;
		const response = await axios.put(
			`${url}/control_center/${reportType}/${reportId}/status`,
			body,
			{ ...AuthHeader }
		);
		return response.data;
	} catch (error) {
		console.error("Failed to update report status", error);
		throw error;
	}
};
